import type { Handler } from './handler';
import type { ApiFetchBlogPostPort } from './ports/api-fetch-blog-post.port';
import { tryCatch } from '~/utils/apostatudo/try-catch';
import { resolveAdapter } from '~/adapters/registry';

const CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_CACHE_ENTRIES = 30;
const RETRY_DELAY_MS = 250;

type CacheEntry = { data: BlogCore.PostDetail; cachedAt: number };

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<BlogCore.PostDetail | null>>();

async function fetchOnceWithRetry(
  port: ApiFetchBlogPostPort,
  slug: string,
): Promise<BlogCore.PostDetail | null> {
  const [first, firstErr] = await tryCatch(port.handle(slug));

  if (!firstErr && first) return first;

  await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));

  const [second] = await tryCatch(port.handle(slug));

  return second ?? null;
}

export const fetchBlogPostHandler: Handler<Params, Ports, Result> = async (
  slug: string,
  ports = {
    apiFetchBlogPost: resolveAdapter('apiFetchBlogPost'),
  },
) => {
  if (!slug) return { post: null };

  const cached = cache.get(slug);

  if (cached && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
    return { post: cached.data };
  }

  const pending = inflight.get(slug);

  if (pending) {
    const [data, err] = await tryCatch(pending);

    if (!err && data) return { post: data };
  }

  const promise = fetchOnceWithRetry(ports!.apiFetchBlogPost, slug);

  inflight.set(slug, promise);

  const [post, fetchError] = await tryCatch(promise);

  inflight.delete(slug);

  if (!fetchError && post) {
    if (cache.size >= MAX_CACHE_ENTRIES) {
      const oldestKey = cache.keys().next().value;

      if (oldestKey) cache.delete(oldestKey);
    }

    cache.set(slug, { data: post, cachedAt: Date.now() });

    return { post };
  }

  console.error('[fetchBlogPostHandler] failed to fetch:', fetchError);

  if (cached) return { post: cached.data };

  return { post: null };
};

type Params = string;

type Ports = { apiFetchBlogPost: ApiFetchBlogPostPort };

type Result = Promise<{ post: BlogCore.PostDetail | null }>;
